import { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useLoaderData, Link } from 'react-router-dom';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { buildCouncilPath } from '../../utils/councilParams';
import { useTranslation } from 'react-i18next';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import { capitalizeFirstLetter, formatMemberName, useFormatters } from '../../utils/texts';
import Card from '../../components/common/Card/Card';
import './MemberDetail.css';
import type { CouncilMember, DropdownOptions } from '../../api';
import MemberDetailVotingSummaryChartSection from '../../components/memberDetailCharts/MemberDetailVotingSummaryChartSection';
import MemberDetailAttendanceSummaryChartSection from '../../components/memberDetailCharts/MemberDetailAttendanceSummaryChartSection';
import MemberComparisonSection from '../../components/memberDetailCharts/MemberComparisonSection';
import { Dropdown } from 'primereact/dropdown';
import { SummaryItemData } from '../../types';
import StatChip from '../../components/common/StatChip/StatChip';
import { formatAliasInterval } from '../../components/AliasTooltip';
import { useAppContext } from '../../context/AppContext';
import { DateTime } from 'luxon';
import { formatDateRange } from '../../utils/dates';
import MaleSvgAvatar from '../../components/common/MemberCard/MaleAvatarSvg';
import FemaleAvatarSvg from '../../components/common/MemberCard/FemaleAvatarSvg';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';

const findByCode = <T extends { code: string }>(items: T[] | undefined, code: string | null) => {
        if (!code) return undefined;
        return items?.find((item) => item.code === code);
    };

function MemberDetail() {
    const [selectedAttendance, setSelectedAttendance] = useState<string | null>(null);

    const { t, i18n } = useTranslation('members');
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const { municipalityData } = useAppContext();

    const timeZone = municipalityData.casova_zona;

    const [selectedPartyId, setSelectedPartyId] = useState<number>(0);

    const memberData = useLoaderData() as CouncilMember;
    const memberWasActiveAtLeastOnce = memberData?.hlasovani?.aktivni_platna > 0;

    const {
            voteOptionLabels,
            attendanceOptionLabels
        } = useLocalizationLabels();

    const formatMembershipDuration = (dateFrom?: string | null, dateTo?: string | null) =>
        formatDateRange(dateFrom, dateTo, currentLocale, timeZone, t);

    const uniquePoliticalParties = useMemo(
        () =>
            memberData?.politicke_strany?.filter(
                (party, index, arr) => arr.findIndex((p) => p.id === party.id) === index
            ) ?? [],
        [memberData?.politicke_strany]
    );

    const partyOptions: DropdownOptions[] = [
        { name: t('MemberDetail.totalSum'), id: 0 },
        ...uniquePoliticalParties.map((party) => ({
            id: party.id,
            name: party.zkratka
        }))
    ];

    const selectedPartyName = partyOptions.find(o => o.id === selectedPartyId)?.name;
    const selectedParty = partyOptions.find(o => o.id === selectedPartyId) ?? partyOptions[0];

    const partyIsSelected = selectedPartyId !== 0;

    const selectedPartyData = useMemo(
        () => memberData?.votes_per_party.find((item) => item.party_id === selectedPartyId),
        [memberData?.votes_per_party, selectedPartyId]
    );

    const selectedPartyMeetingAttendance = useMemo(
        () => (selectedPartyData?.attendance_sum ?? []).reduce((sum, val) => sum + val.count, 0),
        [selectedPartyData]
    );
    const selectedPartyMeetingAttendanceString = t('MemberDetail.meetingFor', {
        num: selectedPartyMeetingAttendance,
        partyName: selectedPartyName,
        count: selectedPartyMeetingAttendance
    });

    const memberIsActiveWholeTerm = memberData.dochazka.mandat === memberData.dochazka.celkem_zasedani;

    const attendancePct = partyIsSelected
        ? (selectedPartyData?.attendance_stat ?? 0)
        : (memberData.dochazka.attendance_stat ?? 0);

    const votingPercentageAttendance = formatter.percent2From100(attendancePct);

    const attendedCountString = t('x-outOf-y', {
        ns: 'translation',
        x: partyIsSelected ? selectedPartyData?.votes_present : memberData.hlasovani.aktivni_platna,
        y: partyIsSelected ? selectedPartyData?.votes_total : memberData.hlasovani.total
    });

    const birthDate = memberData?.birth_date
        ? DateTime.fromISO(memberData.birth_date, { zone: timeZone })
            .setLocale(currentLocale)
            .toLocaleString(DateTime.DATE_FULL)
        : null;

    const state = memberData.stat ? t(`States.${memberData.stat}`, { ns: 'translation' }) : '';

    const summaryItems: SummaryItemData[] = [
        ...(birthDate
        ? [
              {
                  icon: 'cake',
                  text: t('MemberDetail.memberBirthDate'),
                  value: birthDate,
              } as SummaryItemData,
          ]
        : []),
        ...(state
        ? [
              {
                  icon: 'flag',
                  text: t('MemberDetail.electedFor', { context: memberData.pohlavi }),
                  value: state,
              } as SummaryItemData,
          ]
        : []),
        {
            icon: 'description',
            text: memberIsActiveWholeTerm
                ? `${t('mandateWholeTerm', {ns: 'translation'})}`
                : t('mandateOfMeetings', {
                    ns: 'translation',
                    mandate: memberData.dochazka.mandat,
                    total: memberData.dochazka.celkem_zasedani,
                    count: memberData.dochazka.celkem_zasedani
                }),
            value: memberIsActiveWholeTerm
                ? capitalizeFirstLetter(t('active', {ns: 'translation'}))
                : t('x-outOf-y', {
                    ns: 'translation',
                    x: memberData.dochazka.mandat,
                    y: memberData.dochazka.celkem_zasedani
                  }),
            mutedText: partyIsSelected ? selectedPartyMeetingAttendanceString : undefined
        },
        {
            icon: 'check_box',
            text: `${t('votingAttendance', {ns: 'translation'})}`,
            value: votingPercentageAttendance,
            mutedText: attendedCountString
        },
        ...(memberData?.aliasy
            ? [
                  {
                      icon: 'badge',
                      text: memberData?.aliasy.map(
                          (alias) =>
                              `${formatMemberName(alias.jmeno, alias.prijmeni)} ${formatAliasInterval(
                                  alias.datum_od,
                                  alias.datum_do,
                                  t
                              )}`
                      ),
                      value: capitalizeFirstLetter(t('MemberDetail.aliases'))
                  }
              ]
            : [])
    ];

    const displayParty = memberData?.politicke_strany[0].zkratka;
    const displayPartyColor = memberData?.politicke_strany[0].strana_barva;
    const displayLegalParty = memberData?.registrovane_politicke_strany?.[0]?.zkratka;

    const displayPartyView = displayLegalParty ? false : true;

    const memberHasMoreThanOneParty = memberData?.politicke_strany?.length > 1;
    const memberIsActive = memberData?.aktivni_clen === 1;

    const partySelectionDropdown = (
        <Dropdown
            inputId='compareSessionSelection'
            value={selectedPartyId}
            options={partyOptions}
            onChange={(e) => {
                setSelectedAttendance(null);
                setSelectedPartyId(e.value);
            }}
            optionLabel='name'
            optionValue='id'
        />
    );


    const attendanceMapSource = useMemo(() => {
        return partyIsSelected
            ? findByCode(selectedPartyData?.sessions_by_attendance, selectedAttendance)
            : findByCode(memberData.zasedani, selectedAttendance);
    }, [partyIsSelected, selectedPartyData, selectedAttendance, memberData.zasedani]);

    const selectedAttendanceSummary = useMemo(() => {
        return partyIsSelected
            ? findByCode(selectedPartyData?.attendance_sum, selectedAttendance)
            : findByCode(memberData.dochazka.sumarizace, selectedAttendance);
    }, [partyIsSelected, selectedPartyData, selectedAttendance, memberData.dochazka.sumarizace]);

    const selectedAttendanceLabel = useMemo(() => {
        if (!selectedAttendance) return null;

        return capitalizeFirstLetter(
            attendanceOptionLabels[selectedAttendanceSummary?.id ?? ''] ?? selectedAttendance
        );
    }, [selectedAttendance, selectedAttendanceSummary, attendanceOptionLabels]);

    const photo = memberData.photo ?? null;

    const statusText = memberIsActive
        ? t('MemberDetail.memberStatusActive', { context: memberData.pohlavi })
        : t('MemberDetail.memberStatusInactive', { context: memberData.pohlavi });
    const partyLabel = displayPartyView ? displayParty : [displayParty, displayLegalParty].filter(Boolean).join(' · ');
    const headerSubtitle = [statusText, partyLabel].filter(Boolean).join(' · ');
    const titleColor = memberIsActive ? displayPartyColor : 'var(--color-muted60)';

    return (
        <div className="member-detail-dashboard">
            <div className="member-header-stack">
                <div className="member-title-cluster" style={{ '--color-primary': titleColor } as CSSProperties}>
                    <div className='member-title-column'>
                        <ContentSection
                            title={memberData?.jmeno}
                            level={1}
                            subtitle={headerSubtitle}
                            className='member-title-section'
                            titleElement={partySelectionDropdown}
                            titleElementCondition={memberHasMoreThanOneParty}
                            showMoreChevronLinkProps={{
                                text: t('comparison.councilMembers', { ns: 'analyses' }),
                                link: `../../analyzy/porovnani_zastupitelu/?prvni_clen=${memberData.id}`
                            }}
                        />
                    </div>
                    <div className='member-avatar'>
                        {photo ? (
                            <img
                                src={`${memberData.photo}`}
                                className='member-photo'
                            />
                        ) : (
                            memberData?.pohlavi === 0 ? (
                                <MaleSvgAvatar color={"black"} />
                            ) : (
                                <FemaleAvatarSvg color={"black"} />
                            )
                        )}
                    </div>
                </div>
                <div className='stat-chip-row'>
                    {summaryItems.map((item, index) => (
                        <StatChip key={index} icon={item.icon} value={item.value} label={item.text} mutedText={item.mutedText} />
                    ))}
                </div>
                <div className='page-description'>{t('MemberDetail.overview', { ns: 'members' })}</div>
            </div>

            {memberWasActiveAtLeastOnce && (
                <>
                    {(memberHasMoreThanOneParty || !memberIsActive) && (
                        <ContentSection
                            title={t('MemberDetail.affiliationHistory')}
                            contentClassName='affiliation-history-link-list'
                            className='dashboard-card'
                        >
                            {memberData.politicke_strany.map((party, i) => {
                                const membershipDuration = formatMembershipDuration(party.datum_od, party.datum_do);
                                const activeParty = i === 0 && memberIsActive;

                                return (
                                    <Link to={`../../subjekty/${party.id}`} key={party.id}>
                                        <span
                                            style={activeParty ? { color: party.strana_barva } : {}}
                                            className={activeParty ? 'active-party-name' : 'inactive-muted-text'}
                                        >
                                            {party.zkratka}
                                        </span>
                                        {activeParty ? (
                                            <span> ({t('MemberDetail.now')})</span>
                                        ) : (
                                            <span className='inactive-muted-text'> ({membershipDuration})</span>
                                        )}
                                    </Link>
                                );
                            })}
                        </ContentSection>
                    )}
                    <div className="dashboard-grid">
                        <MemberComparisonSection memberData={memberData} partyColor={displayPartyColor} />
                        <MemberDetailVotingSummaryChartSection
                            memberData={memberData}
                            selectedParty={selectedParty}
                            voteOptionLabels={voteOptionLabels}
                        />
                        <MemberDetailAttendanceSummaryChartSection
                            selectedAttendanceState={{ value: selectedAttendance, setValue: setSelectedAttendance }}
                            memberData={memberData}
                            selectedParty={selectedParty}
                            attendanceLabels={attendanceOptionLabels}
                        />
                    </div>
                </>
            )}
            {selectedAttendance !== null && (
                <ContentSection
                    title={`${t('memberAttendance')} - ${selectedAttendanceLabel ?? selectedAttendance}`}
                    contentClassName='meetings-link-list'
                    className='dashboard-card'
                >
                    {attendanceMapSource?.sessions?.map((item) => (
                        <Card
                            key={item.id}
                            link={buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'zasedani', item.id)}
                        >
                            <span>
                                {t('meetingNo', { num: item.meeting_number, ns: 'meetings' })}
                            </span>
                            <span className='material-symbols-rounded'>chevron_right</span>
                        </Card>
                    ))}
                </ContentSection>
            )}
        </div>
    );
}

export default MemberDetail;
